import { useHttp, useTranslate } from '@/shared/lib'
import { computed, type Ref } from 'vue'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { useNotification } from '@/shared/ui'
import type { InventoryMapInfoDetail, InventoryMapInfoFile } from './types.ts'

/**
 * Карточка + статус объекта — одна точка `/passport` отдаёт и то, и другое
 * (`object`/`version.accept_status`), грузится лениво, только пока `enabled`
 * (шапка раскрыта).
 */
export const useLoadInventObjectDetail = (id: Ref<number>, enabled: Ref<boolean>) => {
  const http = useHttp()

  return useQuery<InventoryMapInfoDetail>({
    queryKey: computed(() => ['invent_map_info_detail', id.value]),
    queryFn: () =>
      http.get(`/api/v1/objects/${id.value}/passport`).then((res) => ({
        ...res.data.object,
        acceptStatus: res.data.version?.accept_status,
      })),
    enabled,
    retry: false,
  })
}

/**
 * Сохранение имени/описания объекта — `PATCH` шлёт только изменённое.
 */
export const useSaveInventObjectDetail = () => {
  const http = useHttp()
  const { addError, addSuccess } = useNotification()
  const { trs } = useTranslate()

  const mutation = useMutation({
    mutationFn: (data: { id: number; name?: string; description?: string }) =>
      http.patch(`/api/v1/objects/${data.id}`, {
        name: data.name,
        description: data.description,
      }),
  })

  const save = async (data: { id: number; name?: string; description?: string }) => {
    try {
      await mutation.mutateAsync(data)
      addSuccess(trs('itf.saved'))
      return true
    } catch (error: any) {
      addError(
        trs(
          error?.response?.data?.data?.msg ||
            error?.response?.data?.error ||
            'request.unknown_error',
        ),
      )
      return false
    }
  }

  return {
    ...mutation,

    save,
  }
}

/**
 * Список файлов объекта — `GET /objects/{id}/files`. Грузится лениво вместе
 * с карточкой, пока раскрыта шапка.
 */
export const useLoadInventObjectFiles = (id: Ref<number>, enabled: Ref<boolean>) => {
  const http = useHttp()

  return useQuery<InventoryMapInfoFile[]>({
    queryKey: computed(() => ['invent_map_info_files', id.value]),
    queryFn: () => http.get(`/api/v1/objects/${id.value}/files`).then((res) => res.data),
    enabled,
    retry: false,
  })
}

/**
 * Загрузка одного файла — новая точка принимает файлы по одному (`field: file`),
 * несколько выбранных файлов отправляются последовательными запросами.
 */
export const useUploadInventObjectFile = () => {
  const http = useHttp()
  const { addError } = useNotification()
  const { trs } = useTranslate()

  const mutation = useMutation({
    mutationFn: ({ objectId, file }: { objectId: number; file: File }) => {
      const formData = new FormData()
      formData.append('file', file)
      return http.file(`/api/v1/objects/${objectId}/files`, formData)
    },
  })

  const upload = async (objectId: number, file: File): Promise<InventoryMapInfoFile | null> => {
    try {
      const result = await mutation.mutateAsync({ objectId, file })
      return result.data
    } catch (error: any) {
      addError(trs(error?.response?.data?.data?.msg || 'request.unknown_error'))
      return null
    }
  }

  return { ...mutation, upload }
}

/**
 * Удаление файла — `DELETE /objects/{id}/files/{fileId}`.
 */
export const useRemoveInventObjectFile = () => {
  const http = useHttp()
  const { addError } = useNotification()
  const { trs } = useTranslate()

  const mutation = useMutation({
    mutationFn: ({ objectId, fileId }: { objectId: number; fileId: number }) =>
      http.del(`/api/v1/objects/${objectId}/files/${fileId}`),
  })

  const remove = async (objectId: number, fileId: number): Promise<boolean> => {
    try {
      await mutation.mutateAsync({ objectId, fileId })
      return true
    } catch (error: any) {
      addError(trs(error?.response?.data?.data?.msg || 'request.unknown_error'))
      return false
    }
  }

  return { ...mutation, remove }
}

/**
 * Байты файла для просмотра/скачивания — точка требует `Authorization`, прямая
 * ссылка (`<img src>`/`window.open` на адрес) не подходит, грузим как blob.
 */
export const useLoadInventObjectFileBlob = () => {
  const http = useHttp()
  const { addError } = useNotification()
  const { trs } = useTranslate()

  const mutation = useMutation({
    mutationFn: ({ objectId, fileId }: { objectId: number; fileId: number }) =>
      http.get(`/api/v1/objects/${objectId}/files/${fileId}`, {}, { responseType: 'arraybuffer' }),
  })

  const load = async (objectId: number, fileId: number): Promise<Blob | null> => {
    try {
      const result = await mutation.mutateAsync({ objectId, fileId })
      return new Blob([result.data])
    } catch (error: any) {
      addError(trs(error?.response?.data?.data?.msg || 'request.unknown_error'))
      return null
    }
  }

  return { ...mutation, load }
}

/**
 * Выгрузка координат объекта в GeoJSON — `GET /objects/geojson?ids=`.
 */
export const useLoadInventObjectGeoJson = () => {
  const http = useHttp()
  const { addError } = useNotification()
  const { trs } = useTranslate()

  const mutation = useMutation({
    mutationFn: (id: number) =>
      http.get('/api/v1/objects/geojson', { ids: id }, { responseType: 'arraybuffer' }),
  })

  const load = async (id: number): Promise<Blob | null> => {
    try {
      const result = await mutation.mutateAsync(id)
      return new Blob([result.data])
    } catch (error: any) {
      addError(trs(error?.response?.data?.data?.msg || 'request.unknown_error'))
      return null
    }
  }

  return { ...mutation, load }
}
